import MainLayout from '@/components/common/layouts/MainLayout/MainLayout';
import Banner from '@/components/shop/Banner/Banner';
import type { WCollectionGroupView, WCollectionTileView } from '@zola/svc-web-api-ts-client';
import {
  PrimaryNavIdentifier,
  SecondaryNavIdentifier,
} from '@zola-helpers/client/dist/es/constants/navConstants';
import { UserContext } from '@zola-helpers/client/dist/types/@types';
import { LinkV2 } from '@zola/zola-ui/src/components/LinkV2';
import Headings from '@zola/zola-ui/src/typography/Headings';
import { isUserPostWedding } from '@zola-helpers/client/dist/es/util/dateUtils';
import type { CamelCasedPropertiesDeep } from 'type-fest';
import cx from 'classnames';
import { useMemo } from 'react';
import styles from './giftCollections.module.less';

export type GiftCollectionProps = {
  collectionGroup: CamelCasedPropertiesDeep<WCollectionGroupView>;
  collectionGroups: CamelCasedPropertiesDeep<WCollectionGroupView>[];
};

const Sidebar = ({ collectionGroup, collectionGroups }: GiftCollectionProps): JSX.Element => {
  return (
    <nav className={styles.sidebar}>
      <ul>
        <li>
          <LinkV2
            href="/shop/gift-collections"
            noTextTransform
            noUnderline
            className={cx(styles.active, styles.collectionGroupType)}
          >
            Starter Collections
          </LinkV2>
        </li>
        <li className={styles.collectionGroups}>
          <ul>
            {collectionGroups.map((group) => (
              <li key={group.key}>
                <LinkV2
                  href={`/shop/gift-collections/${group.key}`}
                  noTextTransform
                  noUnderline
                  className={cx({ [styles.active]: group.key === collectionGroup.key })}
                >
                  {group.name}
                </LinkV2>
              </li>
            ))}
          </ul>
        </li>
      </ul>
    </nav>
  );
};

type SampleImage = {
  title: string;
  imageUrl: string;
};

const Tile = ({ tile }: { tile: CamelCasedPropertiesDeep<WCollectionTileView> }): JSX.Element => {
  const sampleImages: SampleImage[] = useMemo(() => {
    const samples = (tile.sampleItems || [])
      .map((item) => {
        const imageUrl = item.image?.aspectRatios?.['1X1']?.small ?? null;
        if (imageUrl) {
          return {
            title: item.name,
            imageUrl,
          } as SampleImage;
        }
        return null;
      })
      .filter((image) => image !== null) as SampleImage[];
    return samples.slice(0, 6);
  }, [tile.sampleItems]);

  return (
    <LinkV2
      className={styles.tile}
      noUnderline
      noTextTransform
      href={`/shop/collection/${tile.key}`}
      title={tile.description}
    >
      <div className={styles.cover}>
        <div
          style={{ backgroundImage: `url(${tile.coverImagePath})` }}
          className={styles.coverImage}
        />
        <Headings.Title2 className={styles.title} presentation="h5" strong>
          {tile.title}
        </Headings.Title2>
      </div>
      <div className={styles.previews}>
        {sampleImages.map((image, index) => (
          <img src={image.imageUrl} alt={image.title} key={index} />
        ))}
      </div>
    </LinkV2>
  );
};

const Grid = ({
  collectionGroup,
}: {
  collectionGroup: CamelCasedPropertiesDeep<WCollectionGroupView> | null;
}): JSX.Element => {
  return (
    <div className={styles.grid}>
      {collectionGroup?.tileViews?.map((tile) => (
        <Tile key={tile.id} tile={tile} />
      ))}
    </div>
  );
};

export const GiftCollections = ({
  collectionGroup,
  collectionGroups,
  isBaby,
  userContext,
}: GiftCollectionProps & { isBaby: boolean; userContext: UserContext | null }): JSX.Element => {
  const isGuest = userContext ? Boolean(userContext.is_guest) : true;
  let adjustedActiveSecondaryLink = SecondaryNavIdentifier.COLLECTIONS;
  if (!isGuest && !isBaby) {
    adjustedActiveSecondaryLink = isUserPostWedding(userContext || undefined)
      ? SecondaryNavIdentifier.POST_WEDDING_SHOP
      : SecondaryNavIdentifier.ADD_GIFTS;
  }

  return (
    <MainLayout
      activePrimaryLink={isBaby ? PrimaryNavIdentifier.SHOP : PrimaryNavIdentifier.REGISTRY}
      activeSecondaryLink={adjustedActiveSecondaryLink}
      activeTertiaryLink={SecondaryNavIdentifier.COLLECTIONS}
      isBaby={isBaby}
    >
      <div className={styles.giftCollections}>
        <div className={styles.banner}>
          <Banner isWeddingBoutiquePage={undefined} />
        </div>
        <Headings.Title1 presentation="h3" strong>
          {collectionGroup.name}
        </Headings.Title1>
        <div className={styles.wrapper}>
          <Sidebar collectionGroup={collectionGroup} collectionGroups={collectionGroups} />
          <Grid collectionGroup={collectionGroup} />
        </div>
      </div>
    </MainLayout>
  );
};
